import { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetBrandQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
} from "../../../../redux/api";
import type { Brand as BrandModel } from "../../../../redux/api";
import { useImageUpload } from "../../../../hooks/useImageUpload";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { GalleryPickerModal } from "../../../../components/GalleryPickerModal";
import { ImagePreviewModal } from "../../../../components/ImagePreviewModal";
import { ImageEditorModal } from "../../../../components/ImageEditorModal";
import { Field } from "../../../../components/Field";
import { statusTone } from "../../../../utils/status";
import type { Asset } from "../../../../redux/api";

interface BrandFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type BrandForm = {
  name: string;
  code: string;
  description: string;
  website: string;
  status: string;
};

const emptyForm: BrandForm = {
  name: "",
  code: "",
  description: "",
  website: "",
  status: "active",
};

const formFromBrand = (b: BrandModel): BrandForm => ({
  name: b.name ?? "",
  code: b.code ?? "",
  description: b.description ?? "",
  website: b.website ?? "",
  status: b.status ?? "active",
});

// Inventory → Brand add/edit popup. Creating a brand keeps the popup open
// and switches into edit mode (rather than closing) so the logo — which
// needs an existing id (the S3 key includes entityId) — can be uploaded
// right away.
export const BrandFormModal = ({ open, id, onClose }: BrandFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetBrandQuery(activeId ?? "", {
    skip: !activeId,
  });

  const [createBrand, { isLoading: creating }] = useCreateBrandMutation();
  const [updateBrand, { isLoading: updating }] = useUpdateBrandMutation();
  const { upload, uploading, error: uploadError } = useImageUpload("BRAND", activeId ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<BrandForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // A brand needs an id (the S3 key includes entityId) before it has
  // anywhere to upload a logo to — same reason Product's image gallery is
  // also gated on isEdit. Create the brand first, then upload. Persists
  // immediately (not part of the dirty-tracked form/"Save changes" below) —
  // there's no free-text URL fallback anymore, since the bucket is private
  // and the server only accepts a key it can presign, not an arbitrary URL.
  const handleLogoFile = async (file: File | undefined) => {
    if (!file || !isEdit || !activeId) return;
    const uploaded = await upload(file);
    if (uploaded) {
      await updateBrand({ id: activeId, input: { logoKey: uploaded.key } }).unwrap();
    }
  };

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const handlePickLogoFromGallery = async ([asset]: Asset[]) => {
    if (!activeId || !asset) return;
    await updateBrand({ id: activeId, input: { logoKey: asset.key } }).unwrap();
  };

  // Points the brand at the edited copy. The original object is untouched and
  // still in the Gallery, so the previous logo can be picked back up from
  // "Choose from gallery" if the edit turns out to be wrong.
  const handleLogoEdited = async (edited: { key: string }) => {
    if (!activeId) return;
    await updateBrand({ id: activeId, input: { logoKey: edited.key } }).unwrap();
  };

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromBrand(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (existing) setForm(formFromBrand(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof BrandForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const baseline = useMemo(
    () => (existing ? formFromBrand(existing) : emptyForm),
    [existing],
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Name is required.";
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || undefined,
      website: form.website.trim() || undefined,
      status: form.status,
    };

    try {
      if (isEdit && activeId) {
        await updateBrand({ id: activeId, input: payload }).unwrap();
        onClose();
      } else {
        const created = await createBrand({ ...payload }).unwrap();
        // Stay open, now in edit mode, so the logo can be uploaded immediately.
        setActiveId(created.id);
      }
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save brand.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit brand" : "New brand"}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || (isEdit && !dirty)}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create brand"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Name *" error={fieldErrors.name}>
          <input
            value={form.name}
            onChange={(e) => {
              setField("name", e.target.value);
              setFieldErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          Code
          <input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            placeholder="BRD-001"
          />
        </label>
        <label className="field">
          Website
          <input
            value={form.website}
            onChange={(e) => setField("website", e.target.value)}
            placeholder="https://…"
          />
        </label>
        <label className="field field--full">
          Logo
          {uploadError ? <div className="form-error">{uploadError}</div> : null}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {existing?.logoUrl ? (
              <img
                src={existing.logoUrl}
                alt="Brand logo"
                style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", cursor: "pointer" }}
                onClick={() => setPreviewOpen(true)}
              />
            ) : (
              <span className="field__readonly">No logo yet</span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={(e) => handleLogoFile(e.target.files?.[0])}
            />
            <button
              type="button"
              className="btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isEdit || uploading}
              title={!isEdit ? "Save the brand first to upload a logo" : undefined}
            >
              {uploading ? "Uploading…" : "Upload logo"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setGalleryOpen(true)}
              disabled={!isEdit}
              title={!isEdit ? "Save the brand first to choose a logo" : undefined}
            >
              Choose from gallery
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setEditorOpen(true)}
              disabled={!existing?.logoKey}
              title={!existing?.logoKey ? "Add a logo first" : "Crop, rotate and adjust"}
            >
              Edit logo
            </button>
          </div>
          <GalleryPickerModal
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            kind="image"
            onSelect={handlePickLogoFromGallery}
          />
          <ImagePreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            url={existing?.logoUrl}
            fileName="Brand logo"
          />
          <ImageEditorModal
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            imageKey={existing?.logoKey ?? null}
            url={existing?.logoUrl ?? null}
            fileName={`${existing?.name ?? "brand"} logo`}
            title="Edit brand logo"
            onApplied={handleLogoEdited}
          />
        </label>
        <label className="field field--full">
          Description
          <input
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
          />
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>
    </Modal>
  );
};
